import type { StageType } from '@/components/shared/stage-help/stage-help-constants';

/**
 * The feature-to-guidance registry: each product area, the routes it owns, and
 * every guidance artefact bound to it (VALE-1867).
 *
 * **Before this file, "what guidance does Discovery have?" had no answer in
 * code.** The Help button's route map, the stage-help banners and the Intercom
 * tour ids lived in three files keyed three different ways (route regexes,
 * `StageType`, semantic tour names), and no key in one resolved into either of
 * the others. `gap-analysis.md` P2 had to assemble the table by hand. Now the
 * three read from here: `lib/constants/help-centre.ts` builds its mappings from
 * {@link GUIDANCE_AREAS}, `components/intercom/tour-ids.ts` selects its
 * environment's ids from {@link INTERCOM_TOURS}, and each stage banner is bound
 * to exactly one area below.
 *
 * **Wiring and facts only, never prose.** Banner copy lives in
 * `guidance/<page>.yaml` (VALE-2066) and article text stays in Intercom. What
 * lives here is which id belongs to which area, which is the part that goes
 * stale silently.
 *
 * **Why a second registry beside `surfaces.ts`, not one merged structure.** The
 * two answer orthogonal questions. `GUIDANCE_SURFACES` is keyed by *surface*
 * (Help button, banner, tour) and binds each to the events that measure it; this
 * is keyed by *product area* and binds each to its artefacts. Merged, every area
 * would repeat the surface's emitter and event list, or every surface would
 * carry a per-area table, and either way one axis becomes a column of the other
 * that nothing guards. So the surfaces point here for their members, and nothing
 * here restates an event.
 *
 * **Loadable by plain Node, on purpose.** This module has type-only imports, so
 * `node --input-type=module -e "import('./lib/guidance/areas.ts')..."` loads it
 * with Node's own type stripping and no build. That is what lets a script
 * resolve {@link listGuidanceHelpDestinations} and {@link listGuidanceTours}
 * against Intercom (`scripts/help-articles-audit.mjs`), and `__tests__/guidance-area-registry.test.ts`
 * fails if a runtime import creeps in.
 */

/** The Help Centre root. Every destination below is relative to it. */
export const HELP_CENTRE_ORIGIN = 'https://help.vale.legal/en/';

/**
 * An Intercom Help Centre page, as its numeric id plus the slug Intercom put in
 * its URL. The id is what Intercom's API resolves; the slug is kept only so the
 * URL we link to is byte-identical to the one Intercom publishes.
 */
export type HelpDestination =
  | { kind: 'article'; id: number; slug: string }
  | { kind: 'collection'; id: number; slug?: string };

/** The public URL for a destination, exactly as the Help Centre serves it. */
export function helpDestinationUrl(destination: HelpDestination): string {
  const path = destination.kind === 'article' ? 'articles' : 'collections';
  const slug = destination.slug ? `-${destination.slug}` : '';
  return `${HELP_CENTRE_ORIGIN}${path}/${destination.id}${slug}`;
}

/** A Help button mapping: a pathname pattern and where it sends the reader. */
export interface HelpRoute {
  /**
   * Published: this is the `route` property of `guidance-help-article-opened`
   * (VALE-1790), so a rename strands every audit row that carried the old one.
   */
  key: string;
  /**
   * The Help button's label on this mapping's pages: what it opens, for the
   * page ("How to use Discovery"), not a bare "Help" (VALE-2138). Required, so
   * a new mapping cannot ship with the generic label by omission. Customer copy
   * on a firm's working screen: Damien approves the wording.
   */
  label: string;
  matches: RegExp;
  destination: HelpDestination;
  /**
   * Tried after every other mapping. The Help button is first-match-wins, and
   * the generic matter mapping matches every matter tab, so it must come last
   * whatever the order of the areas.
   */
  fallback?: true;
}

/**
 * An article linked from inside a component rather than from the Help button,
 * such as a modal's "Read the full guide". Declared here so liveness checks see
 * every article the product links to, not only the Help button's.
 */
export interface InProductArticle {
  destination: Extract<HelpDestination, { kind: 'article' }>;
  /** Repo-relative path of the file that renders the link. */
  source: string;
}

export const IN_PRODUCT_ARTICLES = {
  'smokeball-sync': {
    destination: {
      kind: 'article',
      id: 15941913,
      slug: 'what-syncs-between-vale-and-smokeball'
    },
    source: 'frontend/components/integrations/smokeball/sync-info.ts'
  },
  'actionstep-sync': {
    destination: {
      kind: 'article',
      id: 16404289,
      slug: 'what-syncs-between-vale-and-actionstep'
    },
    source: 'frontend/components/integrations/actionstep/sync-info.ts'
  },
  'keeping-documents-in-sync': {
    destination: {
      kind: 'article',
      id: 16612521,
      slug: 'keeping-documents-in-sync'
    },
    source:
      'frontend/components/integrations/pms/document-sync-audit-dialog.tsx'
  }
} as const satisfies Record<string, InProductArticle>;

export type InProductArticleKey = keyof typeof IN_PRODUCT_ARTICLES;

/**
 * The two Intercom workspaces. `production` is the one customers are supported
 * from; `staging` is its linked test workspace (Settings → Workspace → General →
 * "Enable test workspace"), which shares teammates and products but none of the
 * data. Every non-production environment boots `staging`, so a local or dev
 * session can never write to a production contact (VALE-1953).
 */
export type IntercomWorkspace = 'staging' | 'production';

/** The Messenger `app_id` of each workspace. */
export const INTERCOM_WORKSPACE_APP_IDS = {
  staging: 'w2wt76sz',
  production: 'kf5vr7ia'
} as const satisfies Record<IntercomWorkspace, string>;

/**
 * Intercom product tours. Staging and production are separate Intercom
 * workspaces with their own ids, so both are recorded: a liveness check has to
 * resolve each against its own workspace.
 *
 * `staging: null` means the tour does not exist in the test workspace, so no
 * `?tour=` is sent outside production and the step simply opens its page. No
 * tour is kept there today, by decision (VALE-2006): one is due to be recreated
 * with the tour fixes, so tour tracking can be exercised outside production. The ids this map used
 * to hold for staging (667814, 668344, 668345, 668173) were "(Staging)" copies
 * living in the PRODUCTION workspace, dead since non-production stopped booting
 * it (VALE-1953). How to add a tour to the test workspace:
 * `docs/knowledge-hub/MAINTENANCE.md`, "The test workspace".
 */
export interface IntercomTour {
  staging: number | null;
  production: number;
}

export const INTERCOM_TOURS = {
  navigate: { staging: null, production: 668550 },
  intakeForm: { staging: null, production: 668548 },
  aiScribe: { staging: null, production: 668545 },
  notifyInstitutions: { staging: null, production: 668549 }
} as const satisfies Record<string, IntercomTour>;

export type IntercomTourName = keyof typeof INTERCOM_TOURS;

/** A stage banner's video. */
export interface GuidanceVideo {
  /** The folder under `public/guidance/videos/` holding its files. */
  id: string;
  /**
   * A generated stand-in, so the slot is built and testable before the real
   * video exists. Never shown in production.
   */
  placeholder?: true;
}

export interface GuidanceArea {
  /** Stable id. */
  id: string;
  /** The area as a person would name it. */
  label: string;
  /**
   * The `app/` routes this area owns, in their `[id]` form. A route belongs to
   * the area with the longest matching entry, so `/matters/[id]` claims every
   * matter tab no more specific area declares. Every page in `app/` must land
   * in exactly one area or in {@link ROUTES_WITHOUT_GUIDANCE}.
   */
  routes: readonly string[];
  /** Help button mappings for this area's pages. */
  help: readonly HelpRoute[];
  /** The stage-help banner this area renders, if any. */
  banner?: StageType;
  /** Tours that run inside this area. */
  tours: readonly IntercomTourName[];
  /** Articles linked from inside this area's components. */
  articles: readonly InProductArticleKey[];
  /**
   * The short video this area's stage banner opens from its thumbnail
   * (VALE-2066). `id` names a folder under `public/guidance/videos/`, laid out
   * as {@link guidanceVideoAssets} says. Every banner area carries a
   * `placeholder` clip until GUIDE-15 renders its real video and drops the
   * marker; see {@link guidanceForBanner} for who sees which.
   */
  video?: GuidanceVideo;
  /**
   * A known coverage gap, recorded rather than fixed (fixing is GUIDE-6). Kept
   * beside the data so it reads false the moment someone closes it.
   */
  gap?: string;
}

const matterTab = (tab: string) =>
  new RegExp(`^\\/matters\\/[^/]+\\/${tab}(\\/|$|\\?)`);
const topLevel = (path: string) => new RegExp(`^\\/${path}(\\/|$|\\?)`);

export const GUIDANCE_AREAS: readonly GuidanceArea[] = [
  {
    id: 'discovery',
    label: 'Discovery',
    routes: ['/matters/[id]/discovery'],
    help: [
      {
        key: 'matter-discovery',
        label: 'How to use Discovery',
        matches: matterTab('discovery'),
        destination: {
          kind: 'article',
          id: 15917949,
          slug: 'sending-discovery-correspondence'
        }
      }
    ],
    banner: 'discovery',
    video: { id: 'discovery', placeholder: true },
    tours: ['notifyInstitutions'],
    articles: []
  },
  {
    id: 'communications',
    label: 'Communications',
    routes: ['/matters/[id]/communications'],
    help: [
      {
        key: 'matter-communications',
        label: 'How to use Communications',
        matches: matterTab('communications'),
        destination: {
          kind: 'article',
          id: 15940692,
          slug: 'using-the-communications-tab'
        }
      }
    ],
    tours: [],
    articles: []
  },
  {
    id: 'distribution',
    label: 'Distribution',
    routes: ['/matters/[id]/distribution'],
    help: [
      // Every Distribution page except Beneficiaries, which has its own
      // article below. The lookahead keeps first-match-wins from swallowing it,
      // whatever order the areas are in (VALE-2065).
      {
        key: 'matter-distribution',
        label: 'How to use Distribution',
        matches:
          /^\/matters\/[^/]+\/distribution(?!\/beneficiaries(\/|$|\?))(\/|$|\?)/,
        destination: {
          kind: 'article',
          id: 15966218,
          slug: 'making-estate-payments'
        }
      }
    ],
    banner: 'payments',
    video: { id: 'payments', placeholder: true },
    tours: [],
    articles: []
  },
  {
    id: 'beneficiaries',
    label: 'Distribution: Beneficiaries',
    routes: ['/matters/[id]/distribution/beneficiaries'],
    help: [
      {
        key: 'matter-beneficiaries',
        label: 'How to use Beneficiaries',
        matches: /^\/matters\/[^/]+\/distribution\/beneficiaries(\/|$|\?)/,
        destination: {
          kind: 'article',
          id: 15952702,
          slug: 'securely-collecting-beneficiary-bank-details-and-id'
        }
      }
    ],
    banner: 'beneficiaries',
    video: { id: 'beneficiaries', placeholder: true },
    tours: [],
    articles: []
  },
  {
    id: 'inventory',
    label: 'Inventory',
    routes: ['/matters/[id]/inventory'],
    help: [
      {
        key: 'matter-inventory',
        label: 'How to use Inventory',
        matches: matterTab('inventory'),
        destination: {
          kind: 'article',
          id: 15930271,
          slug: 'managing-the-inventory'
        }
      }
    ],
    tours: [],
    articles: []
  },
  {
    id: 'redemption',
    label: 'Redemption',
    routes: ['/matters/[id]/redemption'],
    help: [
      {
        key: 'matter-redemption',
        label: 'How to use Redemption',
        matches: matterTab('redemption'),
        destination: {
          kind: 'article',
          id: 15952305,
          slug: 'sending-redemption-requests'
        }
      }
    ],
    banner: 'redemption',
    video: { id: 'redemption', placeholder: true },
    tours: [],
    articles: []
  },
  {
    id: 'contacts',
    label: 'Contacts',
    routes: ['/matters/[id]/contacts', '/contacts'],
    help: [
      {
        key: 'matter-contacts',
        label: 'How to use Contacts',
        matches: matterTab('contacts'),
        destination: {
          kind: 'article',
          id: 15941628,
          slug: 'adding-and-managing-contacts'
        }
      }
    ],
    tours: [],
    articles: [],
    gap: 'The firm-wide /contacts page (behind the contacts-page flag) has no Help mapping and goes to the Help Centre home.'
  },
  {
    id: 'scribe',
    label: 'AI Scribe',
    routes: ['/scribe', '/matters/[id]/recordings', '/account/scribe'],
    help: [
      // A processed recording is where its summary and transcript are edited.
      // Before the list mapping, which would otherwise claim it.
      {
        key: 'scribe-recording',
        label: 'How to edit a recording',
        matches: /^\/scribe\/(?!new(\/|$|\?))[^/?]+(\/|$|\?)/,
        destination: {
          kind: 'article',
          id: 15999923,
          slug: 'editing-a-scribe-summary-and-transcript'
        }
      },
      {
        key: 'scribe',
        label: 'How to use AI Scribe',
        matches: topLevel('scribe'),
        destination: {
          kind: 'article',
          id: 15896698,
          slug: 'using-vale-ai-scribe'
        }
      },
      {
        key: 'matter-recordings',
        label: 'How to use AI Scribe',
        matches: matterTab('recordings'),
        destination: {
          kind: 'article',
          id: 15896698,
          slug: 'using-vale-ai-scribe'
        }
      }
    ],
    tours: ['aiScribe'],
    articles: [],
    gap: 'Settings › Scribe has no mapping and goes to the Help Centre home.'
  },
  {
    id: 'intake',
    label: 'Leads and intake',
    routes: ['/intake', '/matters/[id]/intake', '/account/intake-questions'],
    help: [
      {
        key: 'intake',
        label: 'How to send intake forms',
        matches: topLevel('intake'),
        destination: {
          kind: 'article',
          id: 15919391,
          slug: 'sending-client-intake-forms'
        }
      },
      {
        key: 'matter-intake',
        label: 'How to send intake forms',
        matches: matterTab('intake'),
        destination: {
          kind: 'article',
          id: 15919391,
          slug: 'sending-client-intake-forms'
        }
      },
      {
        key: 'account-intake-questions',
        label: 'How to add custom questions',
        matches: /^\/account\/intake-questions(\/|$|\?)/,
        destination: {
          kind: 'article',
          id: 15920022,
          slug: 'adding-custom-questions'
        }
      }
    ],
    tours: ['intakeForm'],
    articles: []
  },
  {
    id: 'enquiries',
    label: 'Enquiries',
    routes: ['/enquiries', '/account/enquiries'],
    help: [
      // The board and an enquiry. The collection on purpose: three of its four
      // articles cover the board, and adding a fifth should cost no code
      // change (VALE-1003 / VALE-1004).
      {
        key: 'enquiries',
        label: 'How to use Enquiries',
        matches: topLevel('enquiries'),
        destination: { kind: 'collection', id: 19736477, slug: 'enquiries' }
      },
      // Settings › Enquiries has a 1:1 article, so it gets the article and not
      // the collection (VALE-988): an administrator on the chase-cadence page
      // should not have to pick their own article out of an index.
      {
        key: 'account-enquiries',
        label: 'How to set up Enquiries',
        matches: /^\/account\/enquiries(\/|$|\?)/,
        destination: {
          kind: 'article',
          id: 16768532,
          slug: 'enquiry-settings-for-administrators'
        }
      }
    ],
    tours: [],
    articles: []
  },
  {
    id: 'grant',
    label: 'Grant',
    routes: ['/matters/[id]/grant'],
    help: [
      {
        key: 'matter-grant',
        label: 'How to use Grant',
        matches: matterTab('grant'),
        destination: {
          kind: 'article',
          id: 15966249,
          slug: 'preparing-the-grant-of-probate-application'
        }
      }
    ],
    tours: [],
    articles: []
  },
  {
    id: 'entitlements',
    label: 'Entitlements',
    routes: ['/matters/[id]/entitlements'],
    help: [
      {
        key: 'matter-entitlements',
        label: 'How to use Entitlements',
        matches: matterTab('entitlements'),
        destination: {
          kind: 'article',
          id: 15930964,
          slug: 'setting-up-entitlements'
        }
      }
    ],
    tours: [],
    articles: []
  },
  {
    id: 'documents',
    label: 'Documents',
    routes: ['/matters/[id]/documents'],
    help: [
      {
        key: 'matter-documents',
        label: 'How to use Documents',
        matches: matterTab('documents'),
        destination: {
          kind: 'article',
          id: 15942716,
          slug: 'managing-documents'
        }
      }
    ],
    tours: [],
    articles: []
  },
  {
    id: 'tasks',
    label: 'Tasks',
    routes: [],
    help: [],
    tours: [],
    articles: [],
    // No article, by decision (VALE-2065): one user in 90 days
    // (`requirements.md` §4.4), and tasks render inside pages that have their own.
    gap: 'No article needed: tasks render inside other pages, whose Help covers them.'
  },
  {
    id: 'planning',
    label: 'Estate planning',
    routes: ['/matters/[id]/planning', '/account/estate-planning'],
    help: [],
    tours: [],
    articles: [],
    gap: 'No Help mapping: the planning pages fall through to the generic matter collection, which is written for a deceased estate.'
  },
  {
    id: 'templates',
    label: 'Templates',
    routes: ['/templates'],
    help: [],
    banner: 'templates',
    video: { id: 'templates', placeholder: true },
    tours: [],
    articles: [],
    gap: 'Has a banner and no Help mapping: /templates goes to the Help Centre home.'
  },
  {
    id: 'settings',
    label: 'Settings',
    routes: ['/account'],
    help: [
      // LF-5429 repointed this from the generic "set up your law firm" article
      // to the trust and office accounts article.
      {
        key: 'account-firm',
        label: 'How to set up trust accounts',
        matches: /^\/account\/firm(\/|$|\?)/,
        destination: {
          kind: 'article',
          id: 15999402,
          slug: 'setting-up-trust-and-office-accounts'
        }
      },
      {
        key: 'account-branding',
        label: 'How to set up your letterhead',
        matches: /^\/account\/branding(\/|$|\?)/,
        destination: {
          kind: 'article',
          id: 15998538,
          slug: 'managing-your-firm-s-letterhead-and-email-signature'
        }
      },
      {
        key: 'account-team',
        label: 'How to invite a team member',
        matches: /^\/account\/team(\/|$|\?)/,
        destination: {
          kind: 'article',
          id: 15998534,
          slug: 'inviting-a-team-member'
        }
      },
      {
        key: 'account-profile',
        label: 'How to set up your profile',
        matches: /^\/account\/profile(\/|$|\?)/,
        destination: {
          kind: 'article',
          id: 15998529,
          slug: 'setting-up-your-profile-and-personal-signature'
        }
      },
      // Exact /account only. It server-redirects to /account/profile, so this
      // is reached only if that redirect changes; kept per the LF-5429 table.
      {
        key: 'account',
        label: 'How to use Settings',
        matches: /^\/account($|\/$|\?)/,
        destination: { kind: 'collection', id: 19622082 }
      }
    ],
    tours: [],
    articles: [],
    gap: 'API keys, Billing, Distribution, Institution contacts and Preferences have no mapping and go to the Help Centre home.'
  },
  {
    id: 'integrations',
    label: 'Practice management integrations',
    routes: ['/account/integrations', '/integrations'],
    help: [
      // The Smokeball collection for every firm, including an ActionStep one.
      // The integration cards' own modal links are provider-matched below.
      {
        key: 'account-integrations',
        label: 'How to use integrations',
        matches: /^\/account\/integrations(\/|$|\?)/,
        destination: { kind: 'collection', id: 19689918, slug: 'smokeball' }
      }
    ],
    tours: [],
    articles: [
      'smokeball-sync',
      'actionstep-sync',
      'keeping-documents-in-sync'
    ],
    gap: 'Settings › Integrations sends every firm to the Smokeball collection, an ActionStep firm included, and the /integrations configure pages go to the Help Centre home.'
  },
  {
    id: 'matter',
    label: 'Matter (overview and any other tab)',
    routes: ['/matters/[id]', '/matters/new', '/matters/demo/[id]'],
    help: [
      {
        key: 'matter-new',
        label: 'How to create a matter',
        matches: /^\/matters\/new(\/|$|\?)/,
        destination: {
          kind: 'article',
          id: 14835114,
          slug: 'creating-a-matter'
        }
      },
      {
        key: 'matter-generic',
        label: 'How to run a matter',
        matches: /^\/matters\/[^/]+(\/|$|\?)/,
        destination: {
          kind: 'collection',
          id: 19688832,
          slug: 'administering-a-matter'
        },
        fallback: true
      }
    ],
    tours: ['navigate'],
    articles: []
  },
  {
    id: 'matter-list',
    label: 'Matter list and dashboard views',
    routes: ['/', '/all', '/today', '/upcoming', '/anytime', '/archive'],
    help: [
      // The most-read article, and the dashboard is where a matter is started.
      {
        key: 'matter-list',
        label: 'How to create a matter',
        matches: /^\/(all|today|upcoming|anytime|archive)?(\/?$|\?)/,
        destination: {
          kind: 'article',
          id: 14835114,
          slug: 'creating-a-matter'
        }
      }
    ],
    tours: [],
    articles: []
  },
  {
    id: 'safe-custody',
    label: 'Safe custody',
    routes: ['/safe-custody'],
    help: [],
    tours: [],
    articles: [],
    gap: 'No Help mapping: goes to the Help Centre home.'
  },
  {
    id: 'getting-started',
    label: 'Getting started and onboarding',
    routes: ['/onboarding'],
    help: [
      {
        // `/onboarding` only: `/get-started` is the signed-out access request
        // page, which the setup article does not describe.
        key: 'getting-started',
        label: 'How to set up your firm',
        matches: /^\/onboarding(\/|$|\?)/,
        destination: {
          kind: 'article',
          id: 15918487,
          slug: 'set-up-your-law-firm'
        }
      }
    ],
    tours: [],
    articles: []
  }
];

/**
 * Pages that belong to no product area, each with the reason, so "is that one
 * missing?" is answered here rather than re-litigated. Matched like
 * {@link GuidanceArea.routes}: an entry claims itself and everything under it.
 */
export const ROUTES_WITHOUT_GUIDANCE: Readonly<Record<string, string>> = {
  '/login': 'Authentication.',
  '/auth-error': 'Authentication.',
  '/signed-out': 'Authentication.',
  '/get-started':
    'Signed out: the request-access page, before any firm exists.',
  '/step-up': 'Authentication.',
  '/impersonate': 'Vale staff support-session entry, not a firm-facing page.',
  '/accept-invitation':
    'Account creation, before the user is inside the product.',
  '/sso-authorisation': 'Token-link authorisation flow.',
  '/m365-authorisation': 'Token-link authorisation flow.',
  '/device-recording':
    'A token-linked recorder page, reached outside a session.',
  '/portal':
    'Executor and beneficiary portal. A different audience, and the firm guidance surfaces do not mount there.',
  '/download-2025-probate-report': 'Marketing download page.'
};

/**
 * Whether a route entry claims an `app/` route: itself or anything under it.
 * The rule {@link GuidanceArea.routes} and {@link ROUTES_WITHOUT_GUIDANCE} are
 * both matched by.
 */
export function routeEntryClaims(entry: string, route: string): boolean {
  if (entry === '/') return route === '/';
  return route === entry || route.startsWith(`${entry}/`);
}

/**
 * The area owning an `app/` route (in its `[id]` form): the one with the
 * longest claiming entry. Shared by the registry test and
 * `scripts/help-articles-audit.mjs`, which maps a changed file to the areas
 * whose pages it reaches.
 */
export function guidanceAreaForRoute(route: string): GuidanceArea | undefined {
  let best: { area: GuidanceArea; length: number } | undefined;
  for (const area of GUIDANCE_AREAS) {
    for (const entry of area.routes) {
      if (
        routeEntryClaims(entry, route) &&
        (!best || entry.length > best.length)
      ) {
        best = { area, length: entry.length };
      }
    }
  }
  return best?.area;
}

/**
 * The Help Centre article an area's own page maps to, for a component that links
 * to the same guide the Help button opens (the (?) info button, GUIDE-19).
 * Reads the mapping rather than restating an id, so a re-pointed article moves
 * every link with it and the liveness check still sees the one destination.
 * `undefined` when the area's mapping is a collection or it has none.
 */
export function areaHelpArticle(
  areaId: string
): Extract<HelpDestination, { kind: 'article' }> | undefined {
  const destination = GUIDANCE_AREAS.find((a) => a.id === areaId)?.help[0]
    ?.destination;
  return destination?.kind === 'article' ? destination : undefined;
}

/**
 * Every Help button mapping, in the order the button tries them: every area's
 * own mappings in registry order, then the fallbacks. Order within the
 * non-fallback set does not matter, because no two of them overlap, and
 * `__tests__/guidance-area-registry.test.ts` pins every app route's destination.
 */
export function listHelpRoutes(): readonly HelpRoute[] {
  const all = GUIDANCE_AREAS.flatMap((area) => area.help);
  return [
    ...all.filter((route) => !route.fallback),
    ...all.filter((route) => route.fallback)
  ];
}

/** One linked Help Centre page, with everything a liveness check needs. */
export interface GuidanceHelpDestinationRef {
  kind: HelpDestination['kind'];
  id: number;
  url: string;
  area: string;
  /** The Help button mapping key, or the in-product article key. */
  usage: string;
  via: 'help-button' | 'in-product-link';
}

/**
 * Every Help Centre page the product links to, one row per link. The same
 * article appears once per place it is linked from, so a dead id names every
 * page it breaks. The input `scripts/help-articles-audit.mjs` resolves against Intercom.
 */
export function listGuidanceHelpDestinations(): readonly GuidanceHelpDestinationRef[] {
  return GUIDANCE_AREAS.flatMap((area) => [
    ...area.help.map((route) => ({
      kind: route.destination.kind,
      id: route.destination.id,
      url: helpDestinationUrl(route.destination),
      area: area.id,
      usage: route.key,
      via: 'help-button' as const
    })),
    ...area.articles.map((key) => {
      const { destination } = IN_PRODUCT_ARTICLES[key];
      return {
        kind: destination.kind,
        id: destination.id,
        url: helpDestinationUrl(destination),
        area: area.id,
        usage: key,
        via: 'in-product-link' as const
      };
    })
  ]);
}

/** One tour, with both workspaces' ids and the area it runs in. */
export interface GuidanceTourRef extends IntercomTour {
  name: IntercomTourName;
  area: string;
}

/** Every tour, with both workspaces' ids, for a liveness check against each workspace. */
export function listGuidanceTours(): readonly GuidanceTourRef[] {
  return GUIDANCE_AREAS.flatMap((area) =>
    area.tours.map((name) => ({ name, area: area.id, ...INTERCOM_TOURS[name] }))
  );
}

/** Where a guidance video's files are served from, under `public/`. */
export const GUIDANCE_VIDEO_ROOT = '/guidance/videos';

/** The files one guidance video consists of, as the browser requests them. */
export interface GuidanceVideoAssets {
  /** The narrated video, opened on click only, so nothing loads with the page. */
  src: string;
  /** The final screen with a play icon and the length: the banner thumbnail. */
  poster: string;
  /** WebVTT captions, on by default. */
  captions: string;
}

/**
 * The fixed layout of a rendered guidance video (VALE-2066). The banner reads
 * it and GUIDE-15's renderer writes it, so the three file names are one
 * contract rather than two guesses.
 */
export function guidanceVideoAssets(id: string): GuidanceVideoAssets {
  const base = `${GUIDANCE_VIDEO_ROOT}/${id}`;
  return {
    src: `${base}/video.mp4`,
    poster: `${base}/poster.jpg`,
    captions: `${base}/captions.vtt`
  };
}

/**
 * The per-firm flag the banner's video thumbnail renders behind (VALE-2066).
 * Seeded off in production by the backend's migration; `backend/src/feature-
 * flags/guidance-videos-flag.spec.ts` holds the backend's copies to this key.
 */
export const GUIDANCE_VIDEOS_FLAG = 'guidance-videos';

/** Who is looking, for {@link guidanceForBanner}'s video decision. */
export interface StageBannerViewer {
  /** The firm holds {@link GUIDANCE_VIDEOS_FLAG}. */
  videosEnabled: boolean;
  /** The app is running in production. */
  production: boolean;
}

/** What a stage banner links to, both optional. */
export interface StageBannerGuidance {
  /** The page's Help Centre article, when the page has one. */
  article?: Extract<HelpDestination, { kind: 'article' }>;
  video?: GuidanceVideo;
}

/**
 * A stage banner's article and video, read from the area that renders it
 * (VALE-2066). The article is the area's own Help button article, so the banner
 * and the Help button on the same page cannot send a reader to two different
 * places; a page whose Help button falls back to a collection or the Help
 * Centre home gets no article link rather than a vague one.
 *
 * The video needs the firm's {@link GUIDANCE_VIDEOS_FLAG}, and a placeholder
 * is withheld in production even then: granting the flag before GUIDE-15's real
 * video lands must show a customer nothing rather than a stand-in.
 */
export function guidanceForBanner(
  stage: StageType,
  viewer: StageBannerViewer
): StageBannerGuidance {
  const area = GUIDANCE_AREAS.find((candidate) => candidate.banner === stage);
  return area ? guidanceForArea(area, viewer) : {};
}

/**
 * An area's article and video by its id, for the Help button (VALE-2138): once
 * a page's banner has been seen, the button is where its video and guide live,
 * so it reads the same pair the banner does rather than a second definition.
 */
export function guidanceForAreaId(
  areaId: string,
  viewer: StageBannerViewer
): StageBannerGuidance {
  const area = GUIDANCE_AREAS.find((candidate) => candidate.id === areaId);
  return area ? guidanceForArea(area, viewer) : {};
}

function guidanceForArea(
  area: GuidanceArea,
  viewer: StageBannerViewer
): StageBannerGuidance {
  const route = area.help.find(
    (candidate) =>
      !candidate.fallback && candidate.destination.kind === 'article'
  );
  const article =
    route?.destination.kind === 'article' ? route.destination : undefined;
  const video =
    area.video &&
    viewer.videosEnabled &&
    !(area.video.placeholder && viewer.production)
      ? area.video
      : undefined;
  return {
    ...(article ? { article } : {}),
    ...(video ? { video } : {})
  };
}
